import { Vector3, Vector2, Box3 } from 'three'
import { types, getParentOfType, Instance, getParent } from 'mobx-state-tree'
import { getSideTranslation } from 'src/utils/getTranslation'
import { UnitNumberValue, Side } from 'src/api/models/values'
import { Project } from 'src/api/models/Project'
import { ElementConnection } from 'src/api/models/Elements'
import {
  generateNodeType,
  generateType
} from 'src/api/models/utils/generateNodeType'
import { getGlobalPlateSide } from 'src/utils/getGlobalPlateSide'
import { generateAdapterPlateShape } from 'src/vectorPaths'
import {
  Side as SideEnum,
  top,
  right,
  bottom,
  left,
  front,
  back
} from 'src/constants/sides'
import {
  adapterPlate,
  adapterPlateEdgeProperties,
  adapterPlateProperties,
  adapterPlateEdge,
  adapterPlateConnection,
  adapterPlateLayer,
  adapterPlateLayerConnection,
  adapterPlateLayerEdge,
  adapterPlateLayerProperties
} from 'src/constants/typenames'
import { getLookAt } from 'src/api/models/utils/getLookAt'
import { getSideProperties } from 'src/utils/getSideProperties'
import { toGlobalPoint } from 'src/api/models/utils/convertGlobal'
import { AdapterHeadConnection } from 'src/api/models/Enclosure/Adapter/AdapterHead'
import {
  adapterPlateThicknessInside,
  adapterPlateThicknessOutside
} from 'src/constants/parameters'
import {
  IAdapterPlateLayerProperties,
  IAdapterPlateLayer,
  IAdapterPlateLayerEdge,
  IAdapterPlateLayerConnection,
  IAdapterPlateProperties,
  IAdapterPlateEdge,
  IAdapterPlateConnection,
  IAdapterPlateEdgeProperties,
  IAdapterPlate
} from './types'
import { IProject } from 'src/api/models/Project/types'
/**
 * AdapterPlateLayer
 */

const AdapterPlateLayerProperties: IAdapterPlateLayerProperties = generateType(
  adapterPlateLayerProperties
)
  .props({
    thickness: UnitNumberValue,
    translation: types.optional(
      types.model({
        position: types.array(types.number) //types.refinement(types.array(types.number), value => (value?.length ? value.length <= 3 : true))
      }),
      { position: [0, 0, 0] }
    )
  })
  .views((self) => {
    const adapterPlate = getParent<Instance<ReturnType<typeof AdapterPlate>>>(
      self,
      5
    )
    const adapterPlateLayer = getParent<
      Instance<ReturnType<typeof AdapterPlateLayer>>
    >(self, 1)
    return {
      get width() {
        return adapterPlate.properties.width
      },
      get length() {
        return adapterPlate.properties.length
      },
      get shape() {
        const { width, length } = adapterPlate.heads.pathData
        const shape = generateAdapterPlateShape(
          adapterPlateLayer.type === 'inside',
          {
            width: { ...width, ...adapterPlate.properties.width },
            length: { ...length, ...adapterPlate.properties.length }
          }
        )
        return shape
      }
    }
  })
// MAKE views ASYNC and wait for attach !!!
// .extend((self) => {})

const AdapterPlateLayer = (): IAdapterPlateLayer =>
  generateType(adapterPlateLayer).props({
    type: types.optional(types.enumeration(['inside', 'outside']), 'inside'),
    properties: AdapterPlateLayerProperties
  })

export const AdapterPlateLayerEdge: IAdapterPlateLayerEdge = generateType(
  adapterPlateLayerEdge
).props({
  node: types.late(AdapterPlateLayer)
})

const AdapterPlateLayerConnection: IAdapterPlateLayerConnection = generateType(
  adapterPlateLayerConnection
).props({
  edges: types.array(AdapterPlateLayerEdge)
})

/**
 * Adapter Plate
 */

const AdapterPlateProperties: IAdapterPlateProperties = generateType(
  adapterPlateProperties
).views((self) => {
  /** 9. Project 8. EnclosureConnection 7. types.array(AdapterEdge) 6. AdapterEdge 5. Adapter 4. AdapterPlateConnection 3. types.array(AdapterPlateEdge) 2. AdapterPlateEdge 1.AdapterPlate */
  // type ProjectType = any //Instance<ReturnType<typeof Project>>
  const project = getParent<IProject>(self, 9)
  const adapterPlateEdge = getParent<Instance<IAdapterPlateEdge>>(self, 2) // getParentOfType(self, AdapterPlateEdge())
  const content = project.content.edges[0]
  return {
    get width() {
      return getSideProperties(
        adapterPlateEdge.properties.side,
        content.boundings
      ).width
    },
    get length() {
      return getSideProperties(
        adapterPlateEdge.properties.side,
        content.boundings
      ).length
    },
    get thickness() {
      const thickness = adapterPlateEdge.node.layers.edges.reduce(
        (p, c) => p + c.node.properties.thickness.value,
        0
      )
      return { value: thickness }
    }
  }
})

export const AdapterPlate = (): IAdapterPlate =>
  generateNodeType(adapterPlate).props({
    properties: types.optional(AdapterPlateProperties, {} as any),
    // elements: types.optional(types.late(ElementConnection), {} as any),
    heads: types.optional(AdapterHeadConnection, {} as any),
    layers: types.optional(AdapterPlateLayerConnection, {
      edges: [
        [adapterPlateThicknessInside, 5, 'inside' as const] as const,
        [adapterPlateThicknessOutside, 0, 'outside' as const] as const
      ].map(([value, y, type]) => ({
        node: {
          type,
          properties: {
            thickness: { value },
            translation: { position: [0, y, 0] }
          }
        }
      }))
    } as any)
  })

/**
 * Adapter Plate Edge
 */

const AdapterPlateEdgeProperties: IAdapterPlateEdgeProperties = generateType(
  adapterPlateEdgeProperties
)
  .props({ side: Side })
  .views((self) => {
    /** 8. Project 7. EnclosureConnection 6. types.array(AdapterEdge) 5. AdapterEdge 4. Adapter 3. AdapterPlateConnection 2. types.array(AdapterPlateEdge) 1. AdapterPlateEdge */
    const project = getParent<Instance<IProject>>(self, 8)

    return {
      get boundings() {
        const contentBoundings = project.content.edges[0].boundings
        const adapterPlateEdge = getParent<
          Instance<ReturnType<typeof AdapterPlateEdge>>
        >(self, 1)

        const thickness = adapterPlateEdge.node.layers.edges.reduce(
          (p, c) => p + c.node.properties.thickness.value,
          0
        )
        const { width, length } = getSideProperties(self.side, contentBoundings)

        const contentCenter = new Vector3()
        const contentSize = new Vector3()
        contentBoundings.getCenter(contentCenter)
        contentBoundings.getSize(contentSize)
        const { y } = contentSize
        const center = new Vector3().copy(contentCenter)
        const boundings = new Box3()
        switch (self.side) {
          case top:
            center.add(new Vector3(0, y / 2 + thickness / 2, 0))
            boundings.setFromCenterAndSize(
              center,
              new Vector3(length.value, thickness, width.value)
            )
            break
          case bottom:
            center.add(new Vector3(0, -(y / 2 + thickness / 2), 0))
            boundings.setFromCenterAndSize(
              center,
              new Vector3(length.value, thickness, width.value)
            )
            break
          default:
            console.warn(
              `AdapterPlateEdgeProperties.boundings:${front},${right},${left},${back} not defined yet`
            )
        }
        return boundings
      },
      get translation() {
        const translation = getSideTranslation(
          self.side,
          project.content.edges[0].boundings
        )
        return translation
      },
      get lookAt() {
        return getLookAt(self.side)
      }
    }
  })

export const AdapterPlateEdge = (): IAdapterPlateEdge =>
  generateNodeType(adapterPlateEdge).props({
    properties: AdapterPlateEdgeProperties,
    node: types.late(AdapterPlate)
  })

export const AdapterPlateConnection: IAdapterPlateConnection = generateType(
  adapterPlateConnection
).props({
  edges: types.optional(types.array(types.late(AdapterPlateEdge)), [
    { properties: { side: top }, node: {} },
    { properties: { side: bottom }, node: {} }
  ] as any)
})
